import { z } from 'zod';
import { isoDateSchema as isoDate, todayIso } from './dates';
import { staffRoleSchema } from './staff.schema';

/**
 * Task types the DB can hold. `custom` is the Figma "General" tile. The Figma "Google profile" tile has no value
 * in `task_type_enum` yet, so it is shown disabled until the enum (and a brief table) exist. TODO(api).
 */
export const TASK_TYPES = ['post', 'reel', 'website', 'monthly_update', 'custom'] as const;
export const taskTypeSchema = z.enum(TASK_TYPES);
export type TaskType = z.infer<typeof taskTypeSchema>;

/** TODO(api): Figma's priority control has a fourth "Urgent" step; `project_priority_enum` has three values. */
export const TASK_PRIORITIES = ['Low', 'Medium', 'High'] as const;
export const taskPrioritySchema = z.enum(TASK_PRIORITIES);
export type TaskPriority = z.infer<typeof taskPrioritySchema>;

/** Task statuses that do not count toward the project's progress rollup (see `projectProgressFromTasks`). */
export const PROGRESS_EXCLUDED_TASK_STATUSES = ['Cancelled', 'User Draft'] as const;

export const POST_PLATFORMS = ['instagram', 'facebook'] as const;
export const POST_FORMATS = ['single_image', 'carousel'] as const;
export const REEL_DURATIONS = [15, 30, 60] as const;
export const CHANGE_KINDS = ['text_prices', 'images', 'layout', 'new_section', 'fix', 'other'] as const;

export const BRIEF_ASSET_MAX = 20;
export const CAPTION_MAX = 2200;
export const HASHTAGS_MAX = 30;
const LONG_TEXT_MAX = 4000;

// --- Brief assets ----------------------------------------------------------------------------------------------

/** A file attached to a brief. `key` is the R2 object key; the file itself is only ever fetched through a presigned URL. */
export const briefAssetSchema = z.object({
  id: z.string().min(1).max(64),
  name: z.string().min(1).max(255),
  key: z.string().min(1).max(500),
  size: z.number().int().nonnegative(),
  contentType: z.string().min(1).max(100),
  /** What it is in the version it belongs to: a slide's name ("Hook"), a screenshot's caption. */
  label: z.string().trim().max(60).optional(),
});
export type BriefAsset = z.infer<typeof briefAssetSchema>;

/** Where a business's brief files must live: nobody can attach another business's files to a task. */
export const businessAssetPrefix = (businessId: string) => `businesses/${businessId}/`;

// --- The typed brief (version 0) -------------------------------------------------------------------------------

const hashtag = z
  .string()
  .trim()
  .transform((tag) => tag.replace(/^#+/, ''))
  .pipe(
    z
      .string()
      .min(1, 'A hashtag can’t be empty')
      .max(100)
      .regex(/^[\p{L}\p{N}_]+$/u, 'Hashtags use letters, numbers and underscores only'),
  );
const hashtags = z.array(hashtag).max(HASHTAGS_MAX, `Up to ${HASHTAGS_MAX} hashtags`).default([]);
const platformList = z.array(z.enum(POST_PLATFORMS));
const scheduledAt = z.iso.datetime({ offset: true }).optional();
const text = (max: number) => z.string().trim().max(max);
const optionalText = (max: number) => text(max).optional();
const hexColor = z.string().regex(/^#[0-9a-fA-F]{6}$/, 'Use a colour like #7A1F1F');
const tooLong = (max: number) => `Keep this under ${max} characters`;

// The field rules a submitted version (submissions.schema.ts) shares with the brief.
export {
  hashtags as hashtagListSchema,
  platformList as platformListSchema,
  scheduledAt as scheduledAtSchema,
  hexColor as hexColorSchema,
  LONG_TEXT_MAX,
};

/**
 * One brief per task type. `shape` is what a draft may hold: every field optional and blanks allowed, but each
 * still checked when present. `required` tightens the fields that must be filled in to submit (the strict form).
 */
function member<T extends z.ZodRawShape, R extends z.ZodRawShape, K extends TaskType>(type: K, shape: T, required: R) {
  const kind = { type: z.literal(type) };
  return {
    draft: z
      .object({ ...kind, ...shape })
      .partial()
      .extend(kind),
    strict: z.object({ ...kind, ...shape, ...required }),
  };
}

const post = member(
  'post',
  {
    format: z.enum(POST_FORMATS),
    platforms: platformList,
    caption: text(CAPTION_MAX).max(CAPTION_MAX, tooLong(CAPTION_MAX)).optional(),
    hashtags,
    audioNote: optionalText(200),
    scheduledUploadAt: scheduledAt,
  },
  { format: z.enum(POST_FORMATS), platforms: platformList.min(1, 'Pick where it posts') },
);

const reel = member(
  'reel',
  {
    script: text(LONG_TEXT_MAX),
    durationSeconds: z.union([z.literal(15), z.literal(30), z.literal(60)]),
    platforms: platformList,
    caption: text(CAPTION_MAX).max(CAPTION_MAX, tooLong(CAPTION_MAX)).optional(),
    hashtags,
    audioNote: optionalText(200),
    scheduledUploadAt: scheduledAt,
  },
  {
    script: text(LONG_TEXT_MAX).min(1, 'Write the hook, script and shots'),
    durationSeconds: z.union([z.literal(15), z.literal(30), z.literal(60)]),
    platforms: platformList.min(1, 'Pick where it posts'),
  },
);

const website = member(
  'website',
  {
    goalAudience: text(LONG_TEXT_MAX),
    domainName: optionalText(255),
    domainRegistrar: optionalText(100),
    style: optionalText(100),
    primaryColor: hexColor.optional(),
    secondaryColor: hexColor.optional(),
    fonts: optionalText(200),
    pages: z
      .array(
        z.object({
          title: text(150).min(1, 'Name the page'),
          sections: z.array(text(150).min(1)).max(30).default([]),
        }),
      )
      .max(30)
      .default([]),
    logoNotes: optionalText(LONG_TEXT_MAX),
    generalNotes: optionalText(LONG_TEXT_MAX),
    referenceSites: z.array(text(300).min(1)).max(10).default([]),
    liveBy: isoDate.optional(),
  },
  { goalAudience: text(LONG_TEXT_MAX).min(1, 'Say who the site is for and what it should do') },
);

const monthlyUpdate = member(
  'monthly_update',
  {
    pageUrl: optionalText(500),
    changeKind: z.enum(CHANGE_KINDS),
    description: text(LONG_TEXT_MAX),
    liveBy: isoDate.optional(),
  },
  { changeKind: z.enum(CHANGE_KINDS), description: text(LONG_TEXT_MAX).min(1, 'Say what should change') },
);

const custom = member(
  'custom',
  { details: text(LONG_TEXT_MAX) },
  { details: text(LONG_TEXT_MAX).min(1, 'Describe what is needed') },
);

const strictBriefSchema = z.discriminatedUnion('type', [
  post.strict,
  reel.strict,
  website.strict,
  monthlyUpdate.strict,
  custom.strict,
]);

/** What a draft may hold: every field optional, but each still checked if it is present. */
export const draftBriefSchema = z.discriminatedUnion('type', [
  post.draft,
  reel.draft,
  website.draft,
  monthlyUpdate.draft,
  custom.draft,
]);

export type BriefInput = z.input<typeof draftBriefSchema>;
export type Brief = z.output<typeof draftBriefSchema>;

// --- Create / update --------------------------------------------------------------------------------------------

export const TASK_TITLE_MAX = 255;
export const TASK_WORK_UNITS_MAX = 99;

// --- Milestones (stops on the progress slider) --------------------------------------------------------------------

export const MILESTONES_MAX = 12;
/** Most people put on one task when it is created. */
export const TASK_TEAM_MAX = 10;

/**
 * A named stop on the progress track ("Lead QA", 80%). A separate checklist from the slider and the status: moving the
 * slider never ticks one, and ticking one never moves the slider (Sprint 2 KB).
 */
export const milestoneInputSchema = z.object({
  title: z.string().trim().min(1, 'Name the milestone').max(255),
  description: z.string().trim().max(500).optional(),
  targetProgress: z.number().int().min(0, 'From 0 to 100').max(100, 'From 0 to 100'),
  completed: z.boolean().default(false),
});
export type MilestoneInput = z.input<typeof milestoneInputSchema>;

const milestonesField = z
  .array(milestoneInputSchema)
  .max(MILESTONES_MAX, `Up to ${MILESTONES_MAX} milestones`)
  .default([]);

/** Body of PUT /api/tasks/{id}/milestones: the whole list, replaced. */
export const setMilestonesInputSchema = z.object({ milestones: milestonesField });
export type SetMilestonesInput = z.input<typeof setMilestonesInputSchema>;

const taskFieldsShape = {
  title: z.string().trim().min(1, 'Give the task a title').max(TASK_TITLE_MAX, 'Keep the title under 255 characters'),
  /** `true` keeps it as a draft (status "User Draft"): nothing is required but a title, and the team does not see it yet. */
  draft: z.boolean().default(false),
  priority: taskPrioritySchema.default('Medium'),
  /** Effort in units (Figma: "1 unit = 2 h"). */
  workUnits: z.number().positive('Effort must be more than 0').max(TASK_WORK_UNITS_MAX).default(1),
  /** "Due for review": when the team has to have it ready. The client sees the publish date instead. */
  dueDate: isoDate.optional(),
  clientVisible: z.boolean().default(true),
  requiresClientApproval: z.boolean().default(true),
  assets: z.array(briefAssetSchema).max(BRIEF_ASSET_MAX, `Up to ${BRIEF_ASSET_MAX} files`).default([]),
  /** Optional stops for the progress slider, set when the task is written (Figma A05.3 step 3). */
  milestones: milestonesField,
  brief: draftBriefSchema,
};

type Fields = z.infer<z.ZodObject<typeof taskFieldsShape>>;

/** Everything that must hold beyond the field shapes: the due date, and (unless it is a draft) a complete brief. */
function refineTask(value: Fields, ctx: z.RefinementCtx) {
  if (value.dueDate && value.dueDate < todayIso()) {
    ctx.addIssue({ code: 'custom', path: ['dueDate'], message: 'Pick today or a later date' });
  }
  if (value.draft) return;
  const strict = strictBriefSchema.safeParse(value.brief);
  if (strict.success) return;
  for (const issue of strict.error.issues) {
    ctx.addIssue({ code: 'custom', path: ['brief', ...issue.path], message: issue.message });
  }
}

/** Body of POST /api/tasks (admin, or the business's account manager). */
export const createTaskInputSchema = z
  .object({
    projectId: z.string().min(1),
    ...taskFieldsShape,
    /**
     * People to put on the task as it is created (Figma A05.3 "Team"). Ignored for a draft. Someone at their task
     * capacity needs `confirmOverCapacity`, exactly as when adding them later.
     */
    assigneeIds: z.array(z.string().min(1)).max(TASK_TEAM_MAX, `Up to ${TASK_TEAM_MAX} people`).default([]),
    confirmOverCapacity: z.boolean().default(false),
  })
  .superRefine(refineTask);

/** Body of PUT /api/tasks/{id}: replaces a draft's brief. The task type cannot change after it is created. */
export const updateTaskInputSchema = z.object(taskFieldsShape).superRefine(refineTask);

export type CreateTaskInput = z.input<typeof createTaskInputSchema>;
export type UpdateTaskInput = z.input<typeof updateTaskInputSchema>;
export type CreateTaskParams = z.output<typeof createTaskInputSchema>;
export type UpdateTaskParams = z.output<typeof updateTaskInputSchema>;

// --- Listing ----------------------------------------------------------------------------------------------------

export const TASK_TABS = ['open', 'done', 'all'] as const;
export const taskTabSchema = z.enum(TASK_TABS);
export type TaskTab = z.infer<typeof taskTabSchema>;

/** Statuses that count as finished work: they leave the "Open" tab. */
export const DONE_TASK_STATUSES = ['Completed', 'Cancelled'] as const;

export const TASK_LIST_LIMIT_MAX = 50;
export const TASK_LIST_LIMIT_DEFAULT = 30;

export const listTasksQuerySchema = z.object({
  projectId: z.string().min(1),
  tab: taskTabSchema.default('open'),
  q: z.string().trim().max(100).optional(),
  /** Only this kind of task (the "Type: All" filter). */
  type: taskTypeSchema.optional(),
  /** A staff id, or 'none' for tasks nobody is on (the "Assignee: Anyone" filter). */
  assigneeId: z.string().min(1).optional(),
  cursor: z.string().max(300).optional(),
  limit: z.coerce.number().int().min(1).max(TASK_LIST_LIMIT_MAX).default(TASK_LIST_LIMIT_DEFAULT),
});

export type ListTasksParams = z.output<typeof listTasksQuerySchema>;

// --- Responses --------------------------------------------------------------------------------------------------

export const taskSummarySchema = z.object({
  id: z.string(),
  projectId: z.string(),
  title: z.string(),
  taskType: taskTypeSchema,
  /** The raw status; each audience maps it to its own label. */
  status: z.string(),
  priority: taskPrioritySchema,
  /** The specialist's manual slider, 0-100. It feeds the project rollup. */
  progress: z.number().int().min(0).max(100),
  workUnits: z.number(),
  deadline: z.string().nullable(),
  clientVisible: z.boolean(),
  requiresClientApproval: z.boolean(),
  revisionCount: z.number().int(),
  maxRevisions: z.number().int(),
  /** Who is on it (the lead first). Null = nobody; always null for a client, who is not told who works on what. */
  assignee: z.string().nullable(),
  /** Milestones on the task's progress track: how many are ticked, of how many ("milestone 2 of 4"). */
  milestoneDone: z.number().int(),
  milestoneTotal: z.number().int(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type TaskSummary = z.infer<typeof taskSummarySchema>;

export const taskListSchema = z.object({
  items: z.array(taskSummarySchema),
  nextCursor: z.string().nullable(),
  /** The tab badges (Open 5 · Done 2 · All 7), within what the caller may see and whatever the current search. */
  counts: z.object({ open: z.number().int(), done: z.number().int(), all: z.number().int() }),
});
export type TaskListResponse = z.infer<typeof taskListSchema>;

/** What is waiting for the client across all their projects (Figma C03.1 "Waiting for you"). */
export const waitingTasksSchema = z.object({
  items: z.array(taskSummarySchema.extend({ projectName: z.string() })),
  /** How many in all; `items` holds only the first few. */
  total: z.number().int(),
});
export type WaitingTasksResponse = z.infer<typeof waitingTasksSchema>;

const nullableText = z.string().nullable();

/** The brief as stored: every field the type has, `null` where it was left blank. */
export const briefDetailsSchema = z.discriminatedUnion('type', [
  z.object({
    type: z.literal('post'),
    format: nullableText,
    platforms: z.array(z.string()),
    caption: nullableText,
    hashtags: z.array(z.string()),
    audioNote: nullableText,
    scheduledUploadAt: nullableText,
  }),
  z.object({
    type: z.literal('reel'),
    script: nullableText,
    durationSeconds: z.number().int().nullable(),
    platforms: z.array(z.string()),
    caption: nullableText,
    hashtags: z.array(z.string()),
    audioNote: nullableText,
    scheduledUploadAt: nullableText,
  }),
  z.object({
    type: z.literal('website'),
    goalAudience: nullableText,
    domainName: nullableText,
    domainRegistrar: nullableText,
    style: nullableText,
    primaryColor: nullableText,
    secondaryColor: nullableText,
    fonts: nullableText,
    pages: z.array(z.object({ title: z.string(), sections: z.array(z.string()) })),
    logoNotes: nullableText,
    generalNotes: nullableText,
    referenceSites: z.array(z.string()),
    liveBy: nullableText,
  }),
  z.object({
    type: z.literal('monthly_update'),
    pageUrl: nullableText,
    changeKind: nullableText,
    description: nullableText,
    liveBy: nullableText,
  }),
  z.object({ type: z.literal('custom'), details: nullableText }),
]);
export type BriefDetails = z.infer<typeof briefDetailsSchema>;

export const taskActivitySchema = z.object({
  id: z.string(),
  message: z.string(),
  /** Who did it, by role: "Account Manager", "System". */
  designation: z.string(),
  authorName: nullableText,
  /** `hotfix` for an in-place edit of a sent version (the client was not told); otherwise null. */
  kind: z.enum(['hotfix']).nullable(),
  createdAt: z.string(),
});

export const taskDetailSchema = taskSummarySchema.extend({
  projectName: z.string(),
  businessId: nullableText,
  businessName: nullableText,
  /** Version 0: the immutable starting point. Once the task leaves "User Draft" it is never edited again. Null for a task created without one. */
  brief: z
    .object({
      authorId: nullableText,
      authorName: nullableText,
      createdAt: z.string(),
      assets: z.array(briefAssetSchema),
      details: briefDetailsSchema,
    })
    .nullable(),
  activity: z.array(taskActivitySchema),
  milestones: z.array(
    z.object({
      id: z.string(),
      title: z.string(),
      description: nullableText,
      targetProgress: z.number().int(),
      completed: z.boolean(),
    }),
  ),
  /** Who is working it, leads first. */
  team: z.array(
    z.object({
      id: z.string(),
      name: z.string(),
      staffRole: staffRoleSchema,
      title: nullableText,
      /** Their pod ("Social pod"): the department they are filed under. */
      department: nullableText,
      assignedAt: z.string(),
      activeTaskCount: z.number().int(),
      maxActiveTasks: z.number().int(),
    }),
  ),
  /** People a lead has suggested, waiting for an admin. */
  pendingRequests: z.array(
    z.object({
      id: z.string(),
      candidate: z.object({ id: z.string(), name: z.string(), staffRole: staffRoleSchema }),
      requestedBy: z.object({ id: z.string(), name: z.string() }),
      createdAt: z.string(),
    }),
  ),
  /** What the caller may do here, worked out by the server so the page never has to guess. */
  access: z.object({
    /** Change details, visibility and the team: admins and the business's account manager. */
    manage: z.boolean(),
    /** Move progress and tick milestones: the above, plus whoever is assigned. */
    work: z.boolean(),
    /** Answer a lead's suggestion: admins. */
    approve: z.boolean(),
    /** Approve a version and send it to the client, or send it back: admins, the account manager, a lead on the task. */
    publish: z.boolean(),
  }),
});
export type TaskDetail = z.infer<typeof taskDetailSchema>;

// --- The task workspace (Figma A05.4) --------------------------------------------------------------------------------

/** Body of PUT /api/tasks/{id}/progress: the specialist's manual slider, independent of the status. */
export const setProgressInputSchema = z.object({
  progress: z.number().int().min(0, 'From 0 to 100').max(100, 'From 0 to 100'),
});
export type SetProgressInput = z.input<typeof setProgressInputSchema>;

/**
 * Body of PATCH /api/tasks/{id}/settings: only what is sent changes. `dueDate: null` clears the deadline.
 */
export const taskSettingsInputSchema = z
  .object({
    priority: taskPrioritySchema,
    workUnits: z.number().positive('Effort must be more than 0').max(TASK_WORK_UNITS_MAX),
    dueDate: isoDate.nullable(),
    clientVisible: z.boolean(),
    requiresClientApproval: z.boolean(),
  })
  .partial()
  .refine((v) => Object.keys(v).length > 0, { message: 'Nothing to change' })
  .superRefine((v, ctx) => {
    if (v.dueDate && v.dueDate < todayIso()) {
      ctx.addIssue({ code: 'custom', path: ['dueDate'], message: 'Pick today or a later date' });
    }
  });
export type TaskSettingsInput = z.input<typeof taskSettingsInputSchema>;
export type TaskSettingsParams = z.output<typeof taskSettingsInputSchema>;

/** Body of POST /api/tasks/{id}/people. */
export const addPersonInputSchema = z.object({
  staffId: z.string().min(1),
  /** The person is at or over their task capacity; the caller has seen the warning and wants them on anyway. */
  confirmOverCapacity: z.boolean().default(false),
});
export type AddPersonInput = z.input<typeof addPersonInputSchema>;
export type AddPersonParams = z.output<typeof addPersonInputSchema>;

/** Body of POST /api/tasks/{id}/people/requests/{requestId}: an admin's answer to a lead's suggestion. */
export const resolvePersonRequestInputSchema = z.object({
  approve: z.boolean(),
  confirmOverCapacity: z.boolean().default(false),
});
export type ResolvePersonRequestInput = z.input<typeof resolvePersonRequestInputSchema>;
export type ResolvePersonRequestParams = z.output<typeof resolvePersonRequestInputSchema>;

/** The people who can work a task (leads and specialists): who is free and who is at capacity. */
export const workerPickerQuerySchema = z.object({
  q: z.string().trim().max(100).optional(),
  limit: z.coerce.number().int().min(1).max(50).default(30),
});
export type WorkerPickerParams = z.output<typeof workerPickerQuerySchema>;

export const workerSchema = z.object({
  id: z.string(),
  name: z.string(),
  staffRole: z.enum(['lead_specialist', 'specialist']),
  title: z.string().nullable(),
  department: z.string().nullable(),
  skills: z.array(z.string()),
  activeTaskCount: z.number().int(),
  maxActiveTasks: z.number().int(),
});
export type Worker = z.infer<typeof workerSchema>;
export const workerListSchema = z.object({ items: z.array(workerSchema) });

/** Code on the 409 when someone is at capacity and the caller has not confirmed. */
export const OVER_CAPACITY_CODE = 'OVER_CAPACITY';
